/**
 * Scores, bands, states, pipelines and tiles (Design System §10, §15). Scores are numbers with a
 * word beside them; the band is a word and is never coloured; position in a sequence carries the
 * meaning, so states need no colour; a tile without its target is decoration.
 */
import { STATE_CLASS, type StateKind } from "@oc/design-tokens";
import { h, cx, type ReactNode } from "./h.ts";

export function StateChip({ label, kind = "ahead" }: { label: string; kind?: StateKind }): ReactNode {
  return h("span", { className: cx("oc-state", STATE_CLASS[kind]) }, label);
}

/** Stages in order; the current one filled, passed ones ruled in ink, ahead in rule grey. `held` and `signed` are drawn as themselves. */
export function Pipeline({ stages, current, onPick, held, signed }: { stages: readonly string[]; current: string; onPick?: (s: string) => void; held?: string; signed?: string }): ReactNode {
  const i = stages.indexOf(current);
  return h("span", { className: "oc-pipeline", role: "list", "aria-label": "Stages" },
    ...stages.map((s, n) => {
      const kind: StateKind = s === held && s === current ? "held" : s === signed && n <= i ? "signed" : n < i ? "past" : n === i ? "current" : "ahead";
      const className = cx("oc-state", STATE_CLASS[kind]);
      return onPick
        ? h("button", { key: s, type: "button", role: "listitem", className, onClick: () => onPick(s), "aria-current": n === i ? "step" : undefined }, s)
        : h("span", { key: s, role: "listitem", className, "aria-current": n === i ? "step" : undefined }, s);
    }));
}

/** Business quality · Automation potential · Overall (or Investment Score, console only) · Band. */
export function ScoreRow({ bq, ap, third, band, thirdLabel = "Overall" }: { bq: ReactNode; ap: ReactNode; third: ReactNode; band: ReactNode; thirdLabel?: "Overall" | "Investment score" }): ReactNode {
  return h("div", { className: "oc-scores" },
    h("span", null, "Business quality", h("b", null, bq)),
    h("span", null, "Automation potential", h("b", null, ap)),
    h("span", null, thirdLabel, h("b", null, third)),
    h("span", null, "Band", h("b", null, band)),
  );
}

/** Metric tile — target and actual on the same tile, always. `late` rules the tile in 2px (the SLA countdown at two hours out). */
export function Tile({ label, value, target, late = false }: { label: string; value: ReactNode; target: string; late?: boolean }): ReactNode {
  return h("div", { className: cx("tile", late && "late") },
    h("div", { className: "lab" }, label),
    h("div", { className: "val oc-tnum" }, value),
    h("div", { className: "target" }, target),
  );
}

export function DocStatus({ status, reviewedBy }: { status: "PROPOSED" | "ACCEPTED"; reviewedBy?: string }): ReactNode {
  return h("span", { className: cx("oc-doc-status", status === "PROPOSED" && "proposed") }, reviewedBy ? `reviewed · ${reviewedBy}` : status);
}

/** The five liveness states a research note carries (DEAL_DISCOVERY_SPEC; Addenda §24). Labels come from the caller — packages/copy owns the words. */
export type BadgeState = "live" | "under_offer" | "withdrawn" | "sold" | "relisted";
export function StatusBadge({ state, label, meta }: { state: BadgeState; label: string; meta?: string }): ReactNode {
  return h("span", { className: cx("oc-badge", state) }, h("b", null, label), meta ? h("span", { className: "meta" }, meta) : null);
}
